import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';
import api from '../utils/api';

const C = {
  bg: '#0a0a0a', surface: '#1a1a1a', surface2: '#222',
  border: '#444', text: '#ffffff', textMuted: '#bbbbbb', textSub: '#eeeeee',
  accent: '#d4f535', green: '#4ade80', orange: '#ff9f43',
  red: '#ff6b6b', blue: '#60a5fa',
};

const SEV = {
  danger:  { icon: 'alert',   color: '#ff6b6b', bg: '#0a0a0a', border: '#ff6b6b', badgeBg: '#ff6b6b33', badgeColor: '#ff6b6b', label: 'Critique'      },
  warning: { icon: 'warning', color: '#ff9f43', bg: '#0a0a0a', border: '#ff9f43', badgeBg: '#ff9f4333', badgeColor: '#ff9f43', label: 'Avertissement' },
  info:    { icon: 'info',    color: '#60a5fa', bg: '#0a0a0a', border: '#60a5fa', badgeBg: '#60a5fa33', badgeColor: '#60a5fa', label: 'Info'           },
};

const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const icons = {
    alert:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    warning: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    info:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
    refresh: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
    check:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    trash:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
    map:     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    globe:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    monitor: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
    mail:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    shield:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  };
  return icons[name] || null;
};

export default function AlertsPage() {
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('');

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await api.get('/alerts?limit=200');
      setAlerts(Array.isArray(data) ? data : []);
    } catch { toast.error('Erreur chargement alertes'); setAlerts([]); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const markRead = async (id) => {
    try {
      await api.put(`/alerts/${id}/read`);
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, read: true } : a));
    } catch { toast.error('Erreur'); }
  };

  const markAllRead = async () => {
    try {
      await api.put('/alerts/read-all');
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
      toast.success('Toutes marquées comme lues');
    } catch { toast.error('Erreur'); }
  };

  const deleteAlert = async (id) => {
    try {
      await api.delete(`/alerts/${id}`);
      setAlerts(prev => prev.filter(a => a.id !== id));
    } catch { toast.error('Erreur suppression'); }
  };

  const filtered = filter ? alerts.filter(a => a.severity === filter) : alerts;
  const unread   = alerts.filter(a => !a.read).length;
  const counts   = {
    danger:  alerts.filter(a => a.severity === 'danger').length,
    warning: alerts.filter(a => a.severity === 'warning').length,
    info:    alerts.filter(a => a.severity === 'info').length,
  };

  return (
    <div style={{ padding: 16, background: C.bg, minHeight: '100vh' }}>

      <div style={{ marginBottom: 20 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap: 10 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 4 }}>
              <h1 style={{ fontSize: 30, fontWeight: 900, color: '#ffffff', margin: 0 }}>Alertes</h1>
              {unread > 0 && (
                <span style={{ background: '#ff6b6b', color: '#fff', borderRadius: 999, padding:'3px 12px', fontSize: 13, fontWeight: 800 }}>
                  {unread} non lu{unread > 1 ? 'es' : 'e'}
                </span>
              )}
            </div>
            <p style={{ color: '#bbbbbb', fontSize: 13, margin: 0 }}>
              {alerts.length} alerte{alerts.length !== 1 ? 's' : ''} · surveillance temps réel
            </p>
          </div>
          <div style={{ display:'flex', gap: 8 }}>
            {unread > 0 && (
              <button onClick={markAllRead} style={{
                padding:'10px 16px', borderRadius: 10,
                border:`1.5px solid ${C.accent}66`, background: C.accent+'1a',
                fontSize: 13, fontWeight: 700, cursor:'pointer', color: C.accent,
                display:'flex', alignItems:'center', gap: 6
              }}>
                <Icon name="check" size={14} color={C.accent}/> Tout lu
              </button>
            )}
            <button onClick={fetchAlerts} style={{
              padding:'10px 14px', borderRadius: 10,
              border:'1.5px solid #444', background: '#1a1a1a',
              cursor:'pointer', display:'flex', alignItems:'center'
            }}>
              <Icon name="refresh" size={16} color="#cccccc"/>
            </button>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap: 10, marginTop: 16 }}>
          {[
            { key:'danger',  label:'Critiques', color:'#ff6b6b', icon:'alert'   },
            { key:'warning', label:'Avertiss.',  color:'#ff9f43', icon:'warning' },
            { key:'info',    label:'Infos',      color:'#60a5fa', icon:'info'    },
          ].map(({ key, label, color, icon }) => (
            <div key={key} style={{ background:'#1c1c1c', borderRadius:12, border:`1px solid #444`, padding:'14px 8px', textAlign:'center' }}>
              <div style={{ display:'flex', justifyContent:'center', marginBottom: 6 }}>
                <Icon name={icon} size={18} color={color}/>
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color, lineHeight: 1 }}>{counts[key]}</div>
              <div style={{ fontSize: 10, color:'#bbbbbb', marginTop: 4, textTransform:'uppercase', letterSpacing: 1 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ height: 1, background:'#333', marginBottom: 16 }}/>

      <div style={{ display:'flex', gap: 8, marginBottom: 18, flexWrap:'wrap' }}>
        {[
          { val:'',        label:'Tout',      count: alerts.length,  color: C.accent  },
          { val:'danger',  label:'Critiques', count: counts.danger,  color:'#ff6b6b'  },
          { val:'warning', label:'Avert.',    count: counts.warning, color:'#ff9f43'  },
          { val:'info',    label:'Info',      count: counts.info,    color:'#60a5fa'  },
        ].map(({ val, label, count, color }) => {
          const active = filter === val;
          return (
            <button key={val} onClick={() => setFilter(val)} style={{
              padding:'8px 16px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor:'pointer',
              border:`1.5px solid ${active ? color : '#444'}`,
              background: active ? color+'28' : '#1a1a1a',
              color: active ? color : '#cccccc',
              display:'flex', alignItems:'center', gap: 6,
            }}>
              {label}
              <span style={{
                background: active ? color+'40' : '#2a2a2a',
                color: active ? color : '#bbbbbb',
                borderRadius: 999, padding:'1px 7px', fontSize: 11, fontWeight: 800,
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding: 48, color:'#bbbbbb', fontSize: 14 }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding: 56, background:'#1a1a1a', borderRadius: 16, border:'1px solid #444' }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom: 14 }}>
            <Icon name="shield" size={38} color={C.accent}/>
          </div>
          <div style={{ fontSize: 16, color:'#ffffff', fontWeight: 700, marginBottom: 6 }}>Aucune alerte</div>
          <div style={{ fontSize: 13, color:'#bbbbbb' }}>Tout est calme — aucune activité suspecte.</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap: 12 }}>
          {filtered.map(alert => {
            const conf = SEV[alert.severity] || SEV.info;
            return (
              <div key={alert.id} style={{
                background: alert.read ? '#141414' : conf.bg,
                border:`1.5px solid ${alert.read ? '#333' : conf.border}`,
                borderLeft:`4px solid ${conf.color}`,
                borderRadius: 14, padding: 18,
                opacity: alert.read ? 0.5 : 1,
                transition:'all 0.2s',
              }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap: 14 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 11,
                    background: conf.badgeBg,
                    border:`1.5px solid ${conf.color}`,
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink: 0,
                  }}>
                    <Icon name={conf.icon} size={20} color={conf.color}/>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap: 8, marginBottom: 6 }}>
                      <div style={{ display:'flex', alignItems:'center', gap: 8, flex: 1 }}>
                        <span style={{ fontWeight: 900, fontSize: 16, color:'#ffffff', lineHeight: 1.3 }}>
                          {alert.message}
                        </span>
                        {!alert.read && (
                          <span style={{ width: 8, height: 8, borderRadius:'50%', background: conf.color, flexShrink: 0 }}/>
                        )}
                      </div>
                      <span style={{
                        background: conf.badgeBg, color: conf.badgeColor,
                        borderRadius: 6, padding:'3px 9px',
                        fontSize: 11, fontWeight: 800, textTransform:'uppercase', letterSpacing: 0.6,
                        border:`1px solid ${conf.color}55`, flexShrink: 0,
                      }}>
                        {conf.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color:'#ffffff', marginBottom: 12, fontWeight: 600 }}>
                      {format(new Date(alert.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                      {alert.member_email && (
                        <span style={{ marginLeft: 8, color:'#ffffff', fontWeight: 900 }}>
                          · {alert.member_email}
                        </span>
                      )}
                    </div>
                    {alert.details && (
                      <div style={{ display:'flex', gap: 8, flexWrap:'wrap', marginTop: 2 }}>
                        {alert.details.ip && (
                          <span style={{ background:'#111', color:'#00ff7f', borderRadius: 8, padding:'5px 11px', fontSize: 15, fontFamily:'monospace', fontWeight: 900, border:'2px solid #00ff7f', display:'flex', alignItems:'center', gap: 6 }}>
                            <Icon name="shield" size={12} color="#00ff7f"/> {alert.details.ip}
                          </span>
                        )}
                        {(alert.details.city || alert.details.country) && (
                          <span style={{ background:'#111', color:'#ff9500', borderRadius: 8, padding:'5px 11px', fontSize: 15, fontWeight: 900, border:'2px solid #ff9500', display:'flex', alignItems:'center', gap: 6 }}>
                            <Icon name="map" size={12} color="#ff9500"/> {alert.details.city || alert.details.country}
                          </span>
                        )}
                        {alert.details.browser && (
                          <span style={{ background:'#111', color:'#4db8ff', borderRadius: 8, padding:'5px 11px', fontSize: 15, fontWeight: 900, border:'2px solid #4db8ff', display:'flex', alignItems:'center', gap: 6 }}>
                            <Icon name="globe" size={12} color="#4db8ff"/> {alert.details.browser}
                          </span>
                        )}
                        {alert.details.os && (
                          <span style={{ background:'#111', color:'#d4f535', borderRadius: 8, padding:'5px 11px', fontSize: 15, fontWeight: 900, border:'2px solid #d4f535', display:'flex', alignItems:'center', gap: 6 }}>
                            <Icon name="monitor" size={12} color="#d4f535"/> {alert.details.os}
                          </span>
                        )}
                        {alert.details.accessEmail && (
                          <span style={{ background:'#111', color:'#ff4444', borderRadius: 8, padding:'5px 11px', fontSize: 15, fontWeight: 900, border:'2px solid #ff4444', display:'flex', alignItems:'center', gap: 6 }}>
                            <Icon name="mail" size={12} color="#ff4444"/> {alert.details.accessEmail}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display:'flex', gap: 8, marginTop: 16 }}>
                  {!alert.read && (
                    <button onClick={() => markRead(alert.id)} style={{
                      flex: 1, padding:'11px 0', borderRadius: 9,
                      border:`1.5px solid ${C.accent}88`, background: C.accent+'22',
                      fontSize: 13, fontWeight: 800, cursor:'pointer', color: C.accent,
                      display:'flex', alignItems:'center', justifyContent:'center', gap: 7,
                    }}>
                      <Icon name="check" size={14} color={C.accent}/> Marquer lu
                    </button>
                  )}
                  <button onClick={() => deleteAlert(alert.id)} style={{
                    flex: alert.read ? 1 : 0,
                    padding: alert.read ? '11px 0' : '11px 20px',
                    borderRadius: 9,
                    border:'1.5px solid #ff6b6b88', background:'#ff6b6b22',
                    fontSize: 13, fontWeight: 800, cursor:'pointer', color:'#ff6b6b',
                    display:'flex', alignItems:'center', justifyContent:'center', gap: 7,
                  }}>
                    <Icon name="trash" size={14} color="#ff6b6b"/>
                    {alert.read ? ' Supprimer' : ''}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
